// Centralised feature access control.
//
// Right now this only gates the AI report features, but it is structured so
// that subscription tiers can be wired in later without changing any callers:
// add the new plan to `kPlanFeatures` and update `getUserPlan` to read the
// plan from wherever subscriptions end up being stored.

import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase.config";

export const kFeatures = {
  AI_REPORTS: "ai_reports",
};

// Plan id -> list of feature ids the plan unlocks.
const kPlanFeatures = {
  free: [],
  pro: [kFeatures.AI_REPORTS],
  coach: [kFeatures.AI_REPORTS],
  admin: Object.values(kFeatures),
};

// ---------------------------------------------------------------------------
// Beta tester allow-list
//
// This used to live only in the build-time REACT_APP_AI_ALLOWED_EMAILS env
// var, so adding/removing a tester meant a code change + redeploy. It is now
// mirrored in Firestore (`config/betaTesters`, field `emails`) so the Admin
// page (src/pages/Admin.jsx) can manage it at runtime; the env var is kept
// only as the seed value the first time that doc is read, and as a fallback
// before Firestore has responded on a given page load.
//
// isAllowedEmailUser()/getUserPlan() are called synchronously all over the
// app (menu items, route gates, etc.), so reads are served from an in-memory
// cache that's seeded from the env var and refreshed from Firestore in the
// background the first time it's touched (same pattern as
// firestoreMigration.js's isMigratedCached).

function parseEmailList(value) {
  return String(value ?? "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

function envAllowedEmails() {
  return parseEmailList(process.env.REACT_APP_AI_ALLOWED_EMAILS);
}

const kBetaTestersDocRef = () => doc(db, "config", "betaTesters");

let cachedAllowedEmails = envAllowedEmails();
let hasRequestedRefresh = false;

function getCachedAllowedEmails() {
  if (!hasRequestedRefresh) {
    hasRequestedRefresh = true;
    refreshBetaTesterEmails();
  }
  return cachedAllowedEmails;
}

// Fetches the authoritative list from Firestore and updates the in-memory
// cache used by isAllowedEmailUser()/getUserPlan(). Seeds the doc from the
// env var list the first time it's read, so existing testers aren't dropped
// when the Admin page takes over as the source of truth.
export async function refreshBetaTesterEmails() {
  try {
    const snap = await getDoc(kBetaTestersDocRef());
    if (snap.exists()) {
      const emails = parseEmailList((snap.data()?.emails ?? []).join(","));
      cachedAllowedEmails = emails;
      return emails;
    }
    const seeded = envAllowedEmails();
    await setDoc(kBetaTestersDocRef(), { emails: seeded });
    cachedAllowedEmails = seeded;
    return seeded;
  } catch (e) {
    console.log("Failed to refresh beta tester list from Firestore", e);
    return cachedAllowedEmails;
  }
}

// Persists an edited beta-tester list (used by the Admin page only).
export async function setBetaTesterEmails(emails) {
  const normalized = Array.from(new Set(parseEmailList((emails ?? []).join(","))));
  await setDoc(kBetaTestersDocRef(), { emails: normalized });
  cachedAllowedEmails = normalized;
  return normalized;
}

// ---------------------------------------------------------------------------
// Admin allow-list
//
// Mirrors the beta-tester list above: authoritative copy lives in Firestore
// (`config/admins`, field `emails`), seeded on first read from
// `kSeedAdminEmails`, cached in-memory for synchronous `isAdminUser()` checks,
// refreshed in the background on first touch. Managed from the Admin page
// itself (src/pages/Admin.jsx), so an existing admin can promote/demote
// others without a code change + redeploy. A FUTURE, more robust option once
// this needs tighter security: a Firebase Auth custom claim set via a
// privileged Cloud Function/admin SDK script, checked via the user's ID token
// instead of their email (email-based checks can't be enforced server-side
// without matching Firestore security rules).
const kSeedAdminEmails = ["chau@peranasports.com"];

const kAdminsDocRef = () => doc(db, "config", "admins");

let cachedAdminEmails = kSeedAdminEmails;
let hasRequestedAdminRefresh = false;

function getCachedAdminEmails() {
  if (!hasRequestedAdminRefresh) {
    hasRequestedAdminRefresh = true;
    refreshAdminEmails();
  }
  return cachedAdminEmails;
}

// Fetches the authoritative admin list from Firestore and updates the cache
// used by isAdminUser(). Seeds the doc from kSeedAdminEmails the first time
// it's read. Returns the fresh list, so callers that need an up-to-date
// answer (e.g. gating the Admin page itself) don't have to rely on the cache.
export async function refreshAdminEmails() {
  try {
    const snap = await getDoc(kAdminsDocRef());
    if (snap.exists()) {
      const emails = parseEmailList((snap.data()?.emails ?? []).join(","));
      cachedAdminEmails = emails.length > 0 ? emails : kSeedAdminEmails;
      return cachedAdminEmails;
    }
    await setDoc(kAdminsDocRef(), { emails: kSeedAdminEmails });
    cachedAdminEmails = kSeedAdminEmails;
    return cachedAdminEmails;
  } catch (e) {
    console.log("Failed to refresh admin list from Firestore", e);
    return cachedAdminEmails;
  }
}

// Persists an edited admin list (used by the Admin page only). Refuses to
// save an empty list — that would lock everyone out of the Admin page with
// no way back in short of editing Firestore directly.
export async function setAdminEmails(emails) {
  const normalized = Array.from(new Set(parseEmailList((emails ?? []).join(","))));
  if (normalized.length === 0) {
    throw new Error("At least one admin is required.");
  }
  await setDoc(kAdminsDocRef(), { emails: normalized });
  cachedAdminEmails = normalized;
  return normalized;
}

export function isAdminUser(user) {
  const email = user && user.email ? user.email.toLowerCase() : "";
  return !!email && getCachedAdminEmails().includes(email);
}

// Resolve the subscription plan for the current user. Checked in order:
//   1. an explicit plan on user settings / the user object,
//   2. the beta-tester allow-list (see above),
//   3. a local dev/testing override (localStorage "VBLivePlan"),
// otherwise defaults to "free".
export function getUserPlan(user, userSettings) {
  const settingsPlan =
    (userSettings && (userSettings.plan || userSettings.subscription)) ||
    (user && user.plan);
  if (settingsPlan) {
    return String(settingsPlan).toLowerCase();
  }

  const email = user && user.email ? user.email.toLowerCase() : "";
  if (email && getCachedAllowedEmails().includes(email)) {
    return "pro";
  }

  const override = localStorage.getItem("VBLivePlan");
  if (override) {
    return override.toLowerCase();
  }

  return "free";
}

export function planAllows(plan, feature) {
  const features = kPlanFeatures[plan] || kPlanFeatures.free;
  return features.includes(feature);
}

export function hasFeatureAccess(feature, user, userSettings) {
  return planAllows(getUserPlan(user, userSettings), feature);
}

// True when the user's email is in the beta-tester allow-list.
export function isAllowedEmailUser(user) {
  const email = user && user.email ? user.email.toLowerCase() : "";
  if (!email) {
    return false;
  }
  const allow = getCachedAllowedEmails();
  return allow.includes(email);
}
